import moment from "moment";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { TrialInstitutionMonitoringsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { TrialInstitutionSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as MonitoringActions from "../../../actions/monitoring";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TrialInstitutionActions from "../../../actions/trialInstitution";
import { RestrictedLayout } from "../../../components/layouts/index";
import { Link } from "../../../components/routing/index";
import { FontAwesomeIcon, FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { ITmdState } from "../../../reducers/index";
import { findLookup } from "src/helpers/lookupHelper";

import "./MonitoringsPage.scss";

interface MonitoringsPageProps {
    monitorings: Dtos.Monitoring[];
    loadingMonitorings: boolean;
    loadMonitoringsSuccess: boolean;
    loadMonitoringsFailure: boolean;
    loadMonitoringsErrorCode: string;
    trialInstitution: Dtos.TrialInstitution;
    loadingTrialInstitution: boolean;
    loadTrialInstitutionSuccess: boolean;
    loadTrialInstitutionFailure: boolean;
    loadTrialInstitutionErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    lookups: Dtos.Lookup[];
    clearMonitorings: MonitoringActions.IMonitoringClearActionCreator;
    loadMonitorings: MonitoringActions.IMonitoringsLoadByTrialInstitutionIdActionCreator;
    clearTrialInstitution: TrialInstitutionActions.ITrialInstitutionClearActionCreator;
    loadTrialInstitution: TrialInstitutionActions.ITrialInstitutionLoadByIdActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const MonitoringsPage: FC<MonitoringsPageProps> = ({
    monitorings,
    loadingMonitorings,
    loadMonitoringsSuccess,
    loadMonitoringsFailure,
    loadMonitoringsErrorCode,
    trialInstitution,
    loadingTrialInstitution,
    loadTrialInstitutionSuccess,
    loadTrialInstitutionFailure,
    loadTrialInstitutionErrorCode,
    permissions,
    pageSize,
    lookups,
    clearMonitorings,
    loadMonitorings,
    clearTrialInstitution,
    loadTrialInstitution,
    setPageSize,
    setBreadcrumbs,
    setDrawerItems,
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { trialInstitutionId } = useParams<{ trialInstitutionId: string }>();
    const trialIdNumber = parseInt(trialId, 10);
    const trialInstitutionIdNumber = parseInt(trialInstitutionId, 10); 
    
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showMonitorings = 
        !loadingMonitorings && 
        loadMonitoringsSuccess &&
        !loadingTrialInstitution &&
        loadTrialInstitutionSuccess;

    // Load Monitorings
    useEffect(() => {
        if (!initalised) {
            setBreadcrumbs(TrialInstitutionMonitoringsBreadcrumbs(trialIdNumber, trialInstitutionIdNumber));

            loadMonitorings(trialInstitutionIdNumber);

            setInitalised(true);
        }
    }, [
        initalised,
        trialIdNumber,
        trialInstitutionIdNumber
    ]);

    // Load Trial Institution
    useEffect(() => {
        if (trialInstitutionIdNumber && !trialInstitution && !loadingTrialInstitution) {
            loadTrialInstitution(trialInstitutionIdNumber);
        }
    }, [
        trialInstitutionIdNumber,
        trialInstitution,
        loadingTrialInstitution
    ]);

    // Update drawer items & breadcrumbs
    useEffect(() => {
        if (trialInstitution) {
            setDrawerItems(TrialInstitutionSubMenu(trialInstitution, permissions));
            setBreadcrumbs(
                TrialInstitutionMonitoringsBreadcrumbs(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    trialInstitution?.trial?.displayName, 
                    trialInstitution?.institution?.name
                )
            );
        }
    }, [
        trialIdNumber, 
        trialInstitutionIdNumber, 
        trialInstitution, 
        permissions
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadMonitoringsFailure) {
            navigate(`/error/${loadMonitoringsErrorCode}`);
        }
        if (loadTrialInstitutionFailure) {
            navigate(`/error/${loadTrialInstitutionErrorCode}`)
        }
    }, [
        loadMonitoringsFailure, 
        loadMonitoringsErrorCode,
        loadTrialInstitutionFailure,
        loadTrialInstitutionErrorCode
    ]);

    // Cleanup effect to clear the monitorings & trial institution on component unmount
    useEffect(() => {
        return () => {
            clearMonitorings();
            clearTrialInstitution();
        };
    }, [clearMonitorings, clearTrialInstitution]);

    const createTable = useMemo(() => {

        const pendingStatusId = findLookup("MonitoringIssue", "monitoringIssueStatusId", lookups)?.values?.find(x => x.value == "Open")?.id;

        if (!monitorings || monitorings.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no monitorings to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = monitorings.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={monitorings}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={showPagination ? pageSize : monitorings.length}
                    pageSize={showPagination ? pageSize : monitorings.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no monitoring to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                navigate(`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring/${rowInfo.original.id}/details`);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "name",
                            desc: false,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Duotone.MONITOR}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "startDate",
                            Header: "Start Date",
                            accessor: "startDate",
                            Cell: (props) => (
                                <span>
                                    {
                                        props.value ?? props?.original?.startDate != undefined 
                                        ? moment(
                                            props.value ??
                                                props?.original?.startDate,
                                            "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                        ).format("DD/MM/YYYY")
                                        : null
                                    }
                                </span>
                            ),
                        },
                        {
                            id: "endDate",
                            Header: "Finish Date",
                            accessor: "endDate",
                            Cell: (props) => (
                                <span>
                                    {
                                        props.value ?? props?.original?.endDate != undefined 
                                        ? moment(
                                            props.value ??
                                                props?.original?.endDate,
                                            "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                        ).format("DD/MM/YYYY")
                                        : null
                                    }
                                </span>
                            ),
                        },
                        {
                            id: "minorDeviations",
                            Header: "Minor Deviations",
                            accessor: "minorDeviations",
                        },
                        {
                            id: "majorDeviations",
                            Header: "Major Deviations",
                            accessor: "majorDeviations",
                            Cell: (props) => (
                                <span>
                                    { 
                                        props?.original?.monitoringIssues?.length > 0 
                                            ? props.original.monitoringIssues.length 
                                            : ""
                                    }
                                </span>
                            ),
                        },
                        {
                            id: "pendingDeviations",
                            Header: "Pending Deviations",
                            accessor: "pendingDeviations",
                            Cell: (props) => (
                                <span>
                                    { 
                                        props?.original?.monitoringIssues?.filter(x => x.monitoringIssueStatusId == pendingStatusId)?.length > 0 
                                            ? props?.original?.monitoringIssues?.filter(x => x.monitoringIssueStatusId == pendingStatusId)?.length
                                            : ""
                                    }
                                </span>
                            ),
                        },
                    ]}
                />
            </div>
        );
    }, [monitorings, pageSize, PAGE_SIZE_OPTIONS, navigate, setPageSize]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Monitorings</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.MonitoringCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring/create`}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showMonitorings ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showMonitorings ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        AuthenticationHelper,
        permissions,
        showMonitorings,
        createTable,
        trialIdNumber,
        trialInstitutionIdNumber,
        lookups
    ]);

    return (
        <RestrictedLayout
            loading={loadingMonitorings || loadingTrialInstitution}
            permission={Dtos.Permission.MonitoringView}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<MonitoringsPageProps> => {
    let monitorings: Dtos.Monitoring[] | undefined =
        state.monitoring.formData instanceof Array
            ? state.monitoring.formData
            : undefined;
    let trialInstitution: Dtos.TrialInstitution | undefined =
        !(state.trialInstitution.formData instanceof Array)
            ? state.trialInstitution.formData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        monitorings: monitorings!,
        loadingMonitorings:
            state.monitoring.loadState &&
            state.monitoring.loadState.status == RequestState.Pending,
        loadMonitoringsSuccess:
            state.monitoring.loadState &&
            state.monitoring.loadState.status == RequestState.Success,
        loadMonitoringsFailure:
            state.monitoring.loadState &&
            state.monitoring.loadState.status == RequestState.Failure,
        loadMonitoringsErrorCode: state.monitoring.loadState.errorCode,
        trialInstitution: trialInstitution!,
        loadingTrialInstitution:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Pending,
        loadTrialInstitutionSuccess:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Success,
        loadTrialInstitutionFailure:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Failure,
        loadTrialInstitutionErrorCode: state.trialInstitution.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
        lookups: state.monitoring.lookups,
    };
};

const mapDispatchToProps = (dispatch): Partial<MonitoringsPageProps> => {
    return {
        loadMonitorings: bindActionCreators(MonitoringActions.LoadMonitoringsByTrialInstitutionId, dispatch),
        clearMonitorings: bindActionCreators(MonitoringActions.Clear, dispatch),
        loadTrialInstitution: bindActionCreators(TrialInstitutionActions.LoadTrialInstitutionById, dispatch),
        clearTrialInstitution: bindActionCreators(TrialInstitutionActions.Clear, dispatch),
        setPageSize: bindActionCreators(PagingActions.PageSizeChanged, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(MonitoringsPage);
